import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Star, Users, Flame, Sticker, Crown, Gem } from "lucide-react";
import { CHAPTERS } from "../data/story";
import { AVATARS } from "../data/curriculum";
import { useProfile } from "../context/ProfileContext";
import { useAuth } from "../context/AuthContext";
import StickerBook from "../components/StickerBook";
import { sfx } from "../services/soundfx";
import { speak } from "../services/phonicsAudio";

export default function QuestMapPage() {
  const navigate = useNavigate();
  const { current, clearProfile, loading } = useProfile();
  const { user } = useAuth();
  const [showStickers, setShowStickers] = useState(false);

  useEffect(() => {
    if (!loading && !current) navigate("/profiles");
  }, [current, loading, navigate]);

  if (!current) return null;
  const premium = !!(user?.premium || user?.subscription?.active || user?.role === "admin");
  const caves = current.caves || {};
  const progress = current.progress || {};
  const ch1Done = !!caves["1-10"]?.completed;

  const chapterUnlocked = (ch, i) => {
    if (i === 0) return true;
    const prev = CHAPTERS[i - 1];
    if (prev.type === "caves") return ch1Done;
    return !!progress[String(prev.levelId)]?.completed;
  };
  const premiumLocked = (ch) => !premium && !ch.free;

  const openChapter = (ch, i) => {
    if (premiumLocked(ch)) {
      sfx.wrong();
      speak("Ask a grown-up to unlock this chapter!");
      navigate("/parent");
      return;
    }
    if (!chapterUnlocked(ch, i)) {
      sfx.wrong();
      speak("Finish the chapter before this one first!");
      return;
    }
    sfx.unlock(); sfx.tap();
    speak(ch.title);
    if (ch.type === "caves") setTimeout(() => navigate(`/chapter/${ch.id}`), 400);
    else setTimeout(() => navigate(`/game/${ch.levelId}`), 400);
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: "linear-gradient(180deg,#312E81 0%,#6D28D9 40%,#9333EA 100%)" }}>
      {/* top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between px-3 py-3 border-b-4 border-amber-300 shadow-md" style={{ background: "#1E1B4BF2" }}>
        <button
          data-testid="switch-profile-btn"
          onClick={() => { sfx.tap(); clearProfile(); navigate("/profiles"); }}
          className="pop-btn flex items-center gap-2 rounded-2xl bg-white/90 border-4 border-amber-200 shadow-[0_4px_0_#FBBF24] active:shadow-none px-2.5 py-2"
        >
          <span className="text-2xl">{AVATARS[current.avatar]?.emoji}</span>
          <Users className="w-4 h-4 text-slate-500" />
        </button>
        <div className="flex items-center gap-1.5">
          <div data-testid="streak-chip" className="flex items-center gap-1 rounded-2xl bg-white/90 border-4 border-orange-200 px-2 py-2 font-display font-bold text-orange-500">
            <Flame className="w-5 h-5 fill-orange-400 text-orange-500" /> {current.streak || 0}
          </div>
          <div data-testid="crystal-chip" className="flex items-center gap-1 rounded-2xl bg-white/90 border-4 border-cyan-200 px-2 py-2 font-display font-bold text-cyan-600">
            <Gem className="w-5 h-5" /> {(current.crystals || []).length}
          </div>
          <button data-testid="open-sticker-book-btn" onClick={() => { sfx.tap(); setShowStickers(true); }} className="pop-btn flex items-center gap-1 rounded-2xl bg-white/90 border-4 border-pink-200 shadow-[0_4px_0_#F9A8D4] active:shadow-none px-2 py-2 font-display font-bold text-pink-500">
            <Sticker className="w-5 h-5" /> {(current.stickers || []).length}
          </button>
          <div className="flex items-center gap-1 rounded-2xl bg-white/90 border-4 border-amber-200 px-2 py-2 font-display font-bold text-amber-600">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" /> {current.total_stars}
          </div>
        </div>
      </div>

      {showStickers && <StickerBook stickers={current.stickers || []} onClose={() => setShowStickers(false)} />}

      <div className="text-center pt-5 px-4">
        <h1 className="font-display text-3xl font-black text-amber-300 drop-shadow">The Sound Crystal Quest</h1>
        <p className="font-bold text-indigo-200 text-sm">Help Lumi ✨ relight the Great Library</p>
      </div>

      {!premium && (
        <button data-testid="unlock-banner" onClick={() => { sfx.tap(); navigate("/parent"); }} className="pop-btn mx-auto mt-4 flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-indigo-900 font-display font-bold px-5 py-2.5 border-4 border-white shadow-[0_5px_0_#B45309] active:shadow-none">
          <Crown className="w-5 h-5" fill="#fff" /> Chapter 1 is free • Unlock the whole quest
        </button>
      )}

      {/* chapter path */}
      <div className="relative max-w-md mx-auto px-6 pt-8">
        {CHAPTERS.map((ch, i) => {
          const pLocked = premiumLocked(ch);
          const unlocked = chapterUnlocked(ch, i);
          const playable = unlocked && !pLocked;
          const side = i % 2 === 0 ? "justify-start" : "justify-end";
          const isCurrent = playable && !(ch.type === "caves" ? ch1Done : progress[String(ch.levelId)]?.completed);
          return (
            <div key={ch.id} className="relative">
              {i > 0 && <div className={`absolute -top-10 ${i % 2 === 0 ? "right-1/4" : "left-1/4"} w-1.5 h-12 path-dots rounded-full opacity-60`} />}
              <div className={`flex ${side} mb-14`}>
                <motion.button
                  data-testid={`chapter-node-${ch.id}`}
                  onClick={() => openChapter(ch, i)}
                  whileTap={{ scale: 0.92 }}
                  animate={isCurrent ? { y: [0, -8, 0] } : {}}
                  transition={isCurrent ? { repeat: Infinity, duration: 1.6 } : {}}
                  className="relative flex flex-col items-center"
                >
                  <div
                    className={`relative flex items-center justify-center rounded-[1.75rem] border-[6px] border-white ${isCurrent ? "animate-pulse-glow" : ""}`}
                    style={{
                      width: 124, height: 124,
                      background: playable || pLocked ? ch.color : "#64748B",
                      boxShadow: playable ? "0 8px 0 rgba(0,0,0,0.3)" : "0 8px 0 rgba(0,0,0,0.25)",
                      filter: playable ? "none" : pLocked ? "saturate(0.85) brightness(0.9)" : "grayscale(0.6)",
                    }}
                  >
                    <span className="text-5xl" style={{ opacity: playable ? 1 : 0.5 }}>{ch.icon}</span>
                    {pLocked && <div className="absolute inset-0 flex items-center justify-center"><Crown className="w-11 h-11 text-amber-200" strokeWidth={2.5} fill="#FDE68A" /></div>}
                    {!unlocked && !pLocked && <Lock className="absolute w-10 h-10 text-white" strokeWidth={2.5} />}
                    <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-white border-4 flex items-center justify-center font-display font-black text-sm" style={{ borderColor: ch.color, color: ch.color }}>
                      {ch.id}
                    </div>
                  </div>
                  <span className="font-display font-bold text-white mt-2 text-center">{ch.title}</span>
                  <span className="text-xs font-bold text-indigo-200">{ch.subtitle}</span>
                </motion.button>
              </div>
            </div>
          );
        })}
        <div className="flex justify-center pb-10"><div className="text-6xl animate-wobble">📚</div></div>
      </div>
    </div>
  );
}
